import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Tasks from 'APP/Tasks';
import { RouterLink, RouterPage } from 'APP/router/constants';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Space } from 'STORE/Spaces/Space';
import { getSortedGroups } from 'UTILS/getSortedGroups';

import { SidebarPanelMode } from './SidebarPanel.constants';

interface ISidebarPanelPresenter {
  panelMode: SidebarPanelMode;
  searchText: string;
  isSearchMode: boolean;
  group: Group | null;
  parentGroup: Group | null;
  space: Space | null;
  isShowHeader: boolean;
  isShowTalks: boolean;
  isShowSpaces: boolean;
  isShowSpace: boolean;
  isShowSearchResults: boolean;
  isShowNewMessageButton: boolean;
  isCollapsed: boolean;
  allGroups: Group[];
  onShowSpace(spaceId: string): void;
  onSearchChange(searchText: string): void;
  onPanelModeChange(panelMode: SidebarPanelMode): void;
}

export const useSidebarPanelPresenter = (): ISidebarPanelPresenter => {
  const navigate = useNavigate();
  const location = useLocation();

  const presenter = useLocalObservable<ISidebarPanelPresenter>(() => ({
    panelMode: SidebarPanelMode.Talks,
    searchText: '',

    get isSearchMode(): boolean {
      return presenter.searchText.trim().length > 0;
    },

    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    get space(): Space | null {
      return Entities.spacesStore.activeSpace;
    },

    get parentGroup(): Group | null {
      return presenter.group?.parentGroup || null;
    },

    get isShowHeader(): boolean {
      return presenter.panelMode !== SidebarPanelMode.Space;
    },

    get isShowTalks(): boolean {
      return (
        !presenter.isShowSearchResults &&
        [SidebarPanelMode.None, SidebarPanelMode.Talks].includes(presenter.panelMode)
      );
    },

    get isShowSpaces(): boolean {
      return !presenter.isShowSearchResults && presenter.panelMode === SidebarPanelMode.Spaces;
    },

    get isShowSpace(): boolean {
      return !presenter.isShowSearchResults && presenter.panelMode === SidebarPanelMode.Space;
    },

    get isCollapsed(): boolean {
      return presenter.panelMode === SidebarPanelMode.Calendar;
    },

    get isShowSearchResults(): boolean {
      return presenter.isSearchMode && presenter.isShowHeader;
    },

    get isShowNewMessageButton(): boolean {
      return presenter.isShowTalks || presenter.isShowSpaces;
    },

    get allGroups(): Group[] {
      if (Entities.GroupsStore.isLoading) {
        return [];
      }

      return getSortedGroups(Entities.GroupsStore.groups);
    },

    onShowSpace(spaceId: string): void {
      navigate(`/${RouterPage.Spaces}/${spaceId}`);
      Tasks.space.setActiveSpace(spaceId);
      presenter.panelMode = SidebarPanelMode.Space;
    },

    onSearchChange(searchText: string): void {
      presenter.searchText = searchText;
    },

    onPanelModeChange(panelMode: SidebarPanelMode): void {
      if (presenter.panelMode === panelMode) {
        return;
      }

      if (presenter.panelMode === SidebarPanelMode.Calendar && !presenter.group) {
        navigate(RouterLink.Main);
      }

      presenter.panelMode = panelMode;
      presenter.searchText = '';
    },
  }));

  useEffect(() => {
    if (presenter.space) {
      presenter.panelMode = SidebarPanelMode.Space;
    } else if (presenter.panelMode === SidebarPanelMode.Space) {
      presenter.panelMode = SidebarPanelMode.Spaces;
    }
  }, [presenter.space]);

  useEffect(() => {
    if (presenter.panelMode !== SidebarPanelMode.Space) {
      Tasks.space.setActiveSpace(null);
    }
  }, [presenter.panelMode]);

  useEffect(() => {
    if (location.pathname.includes(RouterLink.Calendar)) {
      presenter.onPanelModeChange(SidebarPanelMode.Calendar);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (presenter.group && location.pathname.includes(RouterLink.Calendar)) {
      presenter.onPanelModeChange(SidebarPanelMode.Talks);
    }
  }, [presenter.group]);

  return presenter;
};
